import { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react'
import { useQueryClient, useIsFetching } from 'react-query'
import { Hotspot } from 'hooks/queries/designs/useHotspots'
import { useApplyDesignEdits, DesignEditOp, type FrameSourceUrls } from 'hooks/queries/designs/useApplyEdits'
import { useCommitApplyEdits } from 'hooks/queries/designs/useCommitApplyEdits'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { useDesignEditRequests } from 'hooks/queries/designs/useDesignEditRequests'
import { SuggestionRequest, SuggestionRequestStatus, MediaObject } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Modal, StackModalProps } from 'almanac-ui/Modal'
import { Slide } from './Slide'
import { HotspotEdit } from './Types'
import Styles from './ImageEditor.module.scss'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowLeft, TextInput } from 'blaze-ui/icons'
import { ChevronRightSmall, Cursor06 } from 'blaze-ui/icons/24'
import ChevronLeftSmall from 'blaze-ui/icons/24/ChevronLeftSmall'
import { Text } from 'blaze-ui/Text/Text'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { CreditsGate } from 'components/CreditsGate/CreditsGate'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { CreditUsageButton } from 'blaze-ui/CreditUsageButton/CreditUsageButton'
import { ImageEditorComparison } from './ImageEditorComparison'
import {
  parseDesignEditGeneratedContent,
  buildEditDesignVariantsFromRequests,
  getInheritedReferenceImageUrlForFrame,
  getInFlightDesignEditRequestId,
  type DesignEditGeneratedFrame,
  type EditDesignVariant,
} from './parseDesignEditGeneratedContent'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { DesignChannel } from 'components/NewFileModal/Types'
import { pluralizeWithNumber } from 'utils'
import { hotspotsQueryKey } from 'hooks/queries/designs/useHotspots'
import { designEditRequestsQueryKey } from 'hooks/queries/designs/useDesignEditRequests'
import { Button } from 'blaze-ui/Button/Button'

const EMPTY_EDITS: Map<number, HotspotEdit[]> = new Map()

export interface ImageEditorProps extends StackModalProps {
  designKey: string
  mediaObjects: MediaObject[]
  integration?: ExternalIntegration
  channel: DesignChannel
  caption?: string
}

export const ImageEditor = observer(
  ({ designKey, mediaObjects, integration, channel, caption, close }: ImageEditorProps) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [edits, setEdits] = useState<Map<number, Map<number, HotspotEdit[]>>>(() => new Map())
    const [pollingRequestId, setPollingRequestId] = useState<number | null>(null)
    const [preferHotspotEditor, setPreferHotspotEditor] = useState(false)
    const [pendingPollVariant, setPendingPollVariant] = useState<EditDesignVariant | null>(null)
    const [activeVariantIndex, setActiveVariantIndex] = useState(0)
    const [hotspotEditBaseFrames, setHotspotEditBaseFrames] = useState<DesignEditGeneratedFrame[] | null>(null)
    const [hotspotEditParentRequestId, setHotspotEditParentRequestId] = useState<number | null>(null)
    const comparisonVariantCountRef = useRef(0)

    const applyDesignEdits = useApplyDesignEdits()
    const { mutateAsync: commitApplyEdits, isLoading: isCommittingAppliedEdits } = useCommitApplyEdits()
    const {
      data: editDesignRequests,
      refetch: refetchEditDesignRequests,
      isLoading: editRequestsLoading,
      isError: editRequestsError,
    } = useDesignEditRequests(designKey, true)

    const apiVariants = useMemo(() => buildEditDesignVariantsFromRequests(editDesignRequests), [editDesignRequests])

    const variants = useMemo(() => {
      if (apiVariants.length > 0) return apiVariants
      return pendingPollVariant ? [pendingPollVariant] : []
    }, [apiVariants, pendingPollVariant])

    useEffect(() => {
      if (apiVariants.length > 0) setPendingPollVariant(null)
    }, [apiVariants])

    const editHistoryReady = !editRequestsLoading || editRequestsError

    const inFlightRequestId = useMemo(
      () => getInFlightDesignEditRequestId(editDesignRequests),
      [editDesignRequests],
    )

    useEffect(() => {
      if (!editHistoryReady) return
      if (inFlightRequestId == null) return
      setPollingRequestId((prev) => prev ?? inFlightRequestId)
    }, [editHistoryReady, inFlightRequestId])

    const isAwaitingEditPoll = applyDesignEdits.isLoading || pollingRequestId !== null
    const showComparison =
      editHistoryReady && variants.length > 0 && !preferHotspotEditor && !isAwaitingEditPoll
    const showHotspotEditor = !showComparison

    useEffect(() => {
      if (!showComparison) return
      setEdits((prev) => (prev.size === 0 ? prev : new Map()))
    }, [showComparison])

    const isPollingOrSaving = isAwaitingEditPoll
    const isApplyingOnHotspotView = showHotspotEditor && (isPollingOrSaving || isCommittingAppliedEdits)
    const hasMultiple = mediaObjects.length > 1
    const isNavDisabled = isApplyingOnHotspotView || showComparison
    const atFirstSlide = currentIndex === 0
    const atLastSlide = currentIndex === mediaObjects.length - 1
    const { creditUsageStore } = useRootStore()
    const { creditUsage } = creditUsageStore
    const queryClient = useQueryClient()

    const hotspotSlideImageUrl = useMemo(() => {
      const frame = hotspotEditBaseFrames?.find((f) => f.frame_index === currentIndex)
      return frame?.source_url ?? mediaObjects[currentIndex]?.source_url ?? ''
    }, [hotspotEditBaseFrames, currentIndex, mediaObjects])

    const isHotspotDetectionFetching =
      useIsFetching(hotspotsQueryKey(hotspotSlideImageUrl), { exact: true }) > 0

    const handleRefineHotspots = useCallback(() => {
      if (!hotspotSlideImageUrl) return
      void queryClient.refetchQueries({ queryKey: hotspotsQueryKey(hotspotSlideImageUrl), exact: true })
    }, [queryClient, hotspotSlideImageUrl])

    const handlePollResult = useCallback(
      (res: { request: SuggestionRequest }) => {
        if (res.request.status === SuggestionRequestStatus.Successful) {
          setPollingRequestId(null)
          const parsed = parseDesignEditGeneratedContent(res.request.generated_content)
          if (!parsed) {
            showToastMessage('Could not load edited preview', 'error')
            return
          }
          setPendingPollVariant({
            id: res.request.id,
            frames: parsed.frames,
            sourceRequestPrompt: res.request.prompt,
          })
          setPreferHotspotEditor(false)
          setHotspotEditBaseFrames(null)
          setHotspotEditParentRequestId(null)
          setEdits(new Map())
          void refetchEditDesignRequests()
          return
        }
        if (res.request.status === SuggestionRequestStatus.Failed) {
          setPollingRequestId(null)
          showToastMessage('Image edit failed. Please try again.', 'error')
          return
        }
        if (res.request.status === SuggestionRequestStatus.Expired) {
          setPollingRequestId(null)
          showToastMessage('This edit expired. Please try again.', 'error')
          void refetchEditDesignRequests()
          return
        }
      },
      [refetchEditDesignRequests],
    )

    usePollAISuggestionRequest(
      { id: pollingRequestId ?? 0 },
      {
        enabled: pollingRequestId !== null,
        intervalMs: 2000,
        onSuccess: handlePollResult,
      },
    )

    useLayoutEffect(() => {
      if (!showComparison) {
        comparisonVariantCountRef.current = 0
        return
      }
      const n = variants.length
      if (n === 0) return

      const prevN = comparisonVariantCountRef.current
      if (prevN === 0 || n > prevN) {
        setActiveVariantIndex(n - 1)
      } else if (n < prevN) {
        setActiveVariantIndex((i) => Math.min(i, n - 1))
      }
      comparisonVariantCountRef.current = n
    }, [showComparison, variants.length])

    const handleEditChange = useCallback(
      (globalIndex: number, _hotspot: Hotspot, hotspotEdits: HotspotEdit[]) => {
        setEdits((prev) => {
          const next = new Map(prev)
          const slideEdits = new Map(next.get(currentIndex) ?? EMPTY_EDITS)
          if (hotspotEdits.length === 0) {
            slideEdits.delete(globalIndex)
          } else {
            slideEdits.set(globalIndex, hotspotEdits)
          }
          if (slideEdits.size === 0) {
            next.delete(currentIndex)
          } else {
            next.set(currentIndex, slideEdits)
          }
          return next
        })
      },
      [currentIndex],
    )

    const totalEdits = Array.from(edits.values()).reduce((sum, slideEdits) => sum + slideEdits.size, 0)
    const editedFrameCount = edits.size

    const handleSaveAll = useCallback(() => {
      const operations: DesignEditOp[] = []

      for (const [slideIndex, slideEdits] of edits.entries()) {
        for (const [, hotspotEdits] of slideEdits.entries()) {
          const hotspot = hotspotEdits[0]?.hotspot
          if (!hotspot) continue

          const baseOp = {
            frame_index: slideIndex,
            x: hotspot.x,
            y: hotspot.y,
            width: hotspot.width,
            height: hotspot.height,
          }

          if (hotspotEdits.some((e) => e.type === 'remove')) {
            operations.push({ ...baseOp, type: 'remove' as const })
            continue
          }

          for (const edit of hotspotEdits) {
            switch (edit.type) {
              case 'update':
                operations.push({
                  ...baseOp,
                  type: 'update' as const,
                  prompt: edit.prompt,
                })
                break
              case 'update_image':
                operations.push({
                  ...baseOp,
                  type: 'update_image' as const,
                  prompt: edit.prompt,
                  image_urls: edit.imageUrls,
                })
                break
              case 'replace_logo':
                operations.push({
                  ...baseOp,
                  type: 'replace_logo' as const,
                  image_url: edit.imageUrl,
                })
                break
            }
          }
        }
      }

      if (operations.length === 0) return

      const frameIndices = new Set(operations.map((op) => op.frame_index))
      const frame_source_urls: FrameSourceUrls = {}
      for (const frameIndex of frameIndices) {
        const fromVariant = hotspotEditBaseFrames?.find((f) => f.frame_index === frameIndex)
        const source_url = fromVariant?.source_url ?? mediaObjects[frameIndex]?.source_url ?? ''
        if (source_url) {
          frame_source_urls[String(frameIndex)] = source_url
        }
      }

      applyDesignEdits.mutate(
        {
          designKey,
          operations,
          ...(Object.keys(frame_source_urls).length > 0 ? { frame_source_urls } : {}),
          ...(hotspotEditParentRequestId != null ? { parent_request_id: hotspotEditParentRequestId } : {}),
        },
        {
          onSuccess: (data) => {
            void queryClient.invalidateQueries(designEditRequestsQueryKey(designKey))
            setPollingRequestId(data.id)
            showToastMessage(
              "We're applying your edits. You can leave this screen and come back when they're ready.",
              'info',
              undefined,
              8,
            )
          },
          onError: () => {
            showToastMessage('Failed to start design edit', 'error')
          },
        },
      )
    }, [
      edits,
      designKey,
      applyDesignEdits,
      hotspotEditBaseFrames,
      mediaObjects,
      hotspotEditParentRequestId,
      queryClient,
    ])

    const handleUseOriginal = useCallback(async () => {
      try {
        await commitApplyEdits({ designKey, useOriginal: true })
        showToastMessage('Design updated successfully', 'success')
        close()
      } catch {
        showToastMessage('Unable to apply changes', 'error')
      }
    }, [commitApplyEdits, close, designKey])

    const handleEditMore = useCallback(() => {
      setEdits(new Map())
      setPreferHotspotEditor(true)
      setPendingPollVariant(null)
      if (variants.length === 0) {
        setHotspotEditBaseFrames(null)
        setHotspotEditParentRequestId(null)
        return
      }
      const idx = Math.min(activeVariantIndex, variants.length - 1)
      const variant = variants[idx]
      const frames = variant?.frames
      const hasFrames = Boolean(frames?.length)
      setHotspotEditBaseFrames(hasFrames ? frames.map((f) => ({ ...f })) : null)
      setHotspotEditParentRequestId(hasFrames && variant?.id != null ? variant.id : null)
    }, [activeVariantIndex, variants])

    const handleUseModified = useCallback(async () => {
      if (variants.length === 0) return
      const idx = Math.min(activeVariantIndex, variants.length - 1)
      const variant = variants[idx]
      if (!variant) return
      try {
        await commitApplyEdits({
          designKey,
          requestId: variant.id,
        })
        showToastMessage('Design updated successfully', 'success')
        close()
      } catch {
        showToastMessage('Unable to apply changes', 'error')
      }
    }, [variants, activeVariantIndex, commitApplyEdits, close, designKey])

    const headerBackReturnsToComparison =
      preferHotspotEditor && variants.length > 0 && !isApplyingOnHotspotView

    const handleHeaderBack = useCallback(() => {
      if (isApplyingOnHotspotView) {
        close()
        return
      }
      if (preferHotspotEditor && variants.length > 0) {
        setPreferHotspotEditor(false)
        setHotspotEditBaseFrames(null)
        setHotspotEditParentRequestId(null)
        return
      }
      close()
    }, [isApplyingOnHotspotView, preferHotspotEditor, variants.length, close])

    const goToPrevious = useCallback(() => {
      setCurrentIndex((idx) => Math.max(0, idx - 1))
    }, [])

    const goToNext = useCallback(() => {
      setCurrentIndex((idx) => Math.min(mediaObjects.length - 1, idx + 1))
    }, [mediaObjects.length])

    const creditsCharge = editedFrameCount * (creditUsage?.usage_costs.design_edit_credits_cost || 0)

    const safeVariantIndex = variants.length > 0 ? Math.min(activeVariantIndex, variants.length - 1) : 0

    const modifiedMediaObjectsForComparison = useMemo(() => {
      const variant = variants[safeVariantIndex]
      if (!variant) return mediaObjects
      return mediaObjects.map((orig, index) => {
        const frame = variant.frames.find((f) => f.frame_index === index)
        if (!frame?.source_url) return orig
        return {
          ...orig,
          source_url: frame.source_url,
          thumbnail_url: frame.source_url,
        }
      })
    }, [mediaObjects, variants, safeVariantIndex])

    const originalRef = mediaObjects[currentIndex]?.provider_data?.original_image_url ?? null

    const hotspotReferenceImageUrl = useMemo(() => {
      if (hotspotEditBaseFrames == null) {
        return originalRef
      }
      if (hotspotEditParentRequestId != null) {
        const fromChain = getInheritedReferenceImageUrlForFrame(
          editDesignRequests ?? [],
          hotspotEditParentRequestId,
          currentIndex,
        )
        if (fromChain) return fromChain
      }
      return originalRef
    }, [hotspotEditBaseFrames, hotspotEditParentRequestId, editDesignRequests, currentIndex, originalRef])

    return (
      <Modal.Root
        size="fullscreen"
        onClose={close}
        isKeyboardDismissDisabled={false}
        bodyClassName={Styles.modalBody}
      >
        <div className={Styles.headerShell}>
          <header className={Styles.header}>
            <div className={Styles.headerLeft}>
              <IconButton
                variant="secondary"
                size="md"
                onPress={handleHeaderBack}
                icon={ArrowLeft}
                className={Styles.headerBackButton}
                aria-label={headerBackReturnsToComparison ? 'Back to variation choice' : 'Cancel'}
              />
            </div>
            <div className={Styles.headerCenter}>
              {!showComparison && (
                <>
                  <Cursor06 color="var(--dark-90)" boxColor="#E763FE" />
                  <Text variant="secondary" color="var(--dark-60)" className={Styles.headerInstructionText}>
                    Click on any of the pink boxes to edit the text
                  </Text>
                  <Button
                    type="button"
                    variant="secondary"
                    size="xs"
                    className={Styles.refineHotspotsButton}
                    onPress={handleRefineHotspots}
                    isDisabled={
                      !hotspotSlideImageUrl || isApplyingOnHotspotView || isHotspotDetectionFetching
                    }
                    aria-label="Refine the pink boxes"
                  >
                    Refine the pink boxes
                  </Button>
                </>
              )}
            </div>
            <div className={Styles.headerRight}>
              <div className={Styles.headerRightActions}>
                {!showComparison && (
                  <>
                    {totalEdits > 0 && (
                      <Text variant="secondary" color="var(--dark-40)" className={Styles.editCounter} aria-live="polite">
                        {pluralizeWithNumber(totalEdits, 'edit')}
                      </Text>
                    )}
                    {!isApplyingOnHotspotView && (
                      <CreditsGate charge={creditsCharge} onPress={() => handleSaveAll()}>
                        <CreditUsageButton
                          credits={creditsCharge}
                          variant="primary"
                          size="md"
                          className={Styles.headerSaveButton}
                          isDisabled={totalEdits === 0}
                          aria-label="Save changes"
                        >
                          Save Changes
                        </CreditUsageButton>
                      </CreditsGate>
                    )}
                  </>
                )}
                {isApplyingOnHotspotView && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="xs"
                    className={Styles.minimizeButton}
                    onPress={close}
                    aria-label="Minimize"
                  >
                    Minimize
                  </Button>
                )}
              </div>
            </div>
          </header>
        </div>

        {showComparison ? (
          <div className={Styles.comparisonStage}>
            <ImageEditorComparison
              variantCount={variants.length}
              activeVariantIndex={safeVariantIndex}
              onActiveVariantIndexChange={setActiveVariantIndex}
              originalMediaObjects={mediaObjects}
              modifiedMediaObjects={modifiedMediaObjectsForComparison}
              onUseOriginal={handleUseOriginal}
              onUseModified={() => void handleUseModified()}
              onEditMore={handleEditMore}
              isApplyingModified={isCommittingAppliedEdits}
              designKey={designKey}
              integration={integration}
              channel={channel}
              caption={caption}
            />
          </div>
        ) : (
          <div className={Styles.canvas}>
            {hasMultiple && (
              <div style={{ visibility: !atFirstSlide ? 'visible' : 'hidden' }}>
                <IconButton
                  size="lg"
                  variant="secondary"
                  className={Styles.navButton}
                  onPress={() => goToPrevious()}
                  isDisabled={atFirstSlide || isNavDisabled}
                  aria-label="Previous image"
                  icon={ChevronLeftSmall}
                />
              </div>
            )}

            <div className={Styles.imageWrapper}>
              {isApplyingOnHotspotView && (
                <div className={Styles.ocrOverlay}>
                  <Text variant="primary" color="var(--light-90)">
                    <span className={Styles.ocrOverlayText}>
                      <TextInput /> Applying edits
                      <LoadingEllipses />
                    </span>
                  </Text>
                </div>
              )}
              <Slide
                imageUrl={hotspotSlideImageUrl}
                referenceImageUrl={hotspotReferenceImageUrl}
                edits={edits.get(currentIndex) ?? EMPTY_EDITS}
                onEditChange={handleEditChange}
                isDisabled={isApplyingOnHotspotView}
              />
            </div>

            {hasMultiple && (
              <div style={{ visibility: !atLastSlide ? 'visible' : 'hidden' }}>
                <IconButton
                  size="lg"
                  variant="secondary"
                  onPress={() => goToNext()}
                  isDisabled={atLastSlide || isNavDisabled}
                  className={Styles.navButton}
                  aria-label="Next image"
                  icon={ChevronRightSmall}
                />
              </div>
            )}
          </div>
        )}

        {hasMultiple && showHotspotEditor && (
          <div className={Styles.thumbnailStrip}>
            {mediaObjects.map((media, index) => {
              const hasEdits = (edits.get(index)?.size ?? 0) > 0
              return (
                <button
                  key={media.id}
                  type="button"
                  className={`${Styles.thumbnail} ${index === currentIndex ? Styles.thumbnailActive : ''} ${
                    hasEdits ? Styles.thumbnailEdited : ''
                  }`}
                  onClick={() => setCurrentIndex(index)}
                  disabled={isApplyingOnHotspotView}
                >
                  <img src={media.thumbnail_url ?? media.source_url} alt={`Slide ${index + 1}`} />
                </button>
              )
            })}
          </div>
        )}
      </Modal.Root>
    )
  },
)
